/**
 * Build-time flags for the kiosk / non-kiosk surface (spec 2026-10-08-kiosk-web-split §3). Kept
 * apart from surfaceDetection.ts and SurfaceContext so jest (ts-jest CommonJS cannot parse
 * `import.meta`) never loads it: main.tsx passes surfaceEnvironment() to SurfaceProvider, and
 * hooks that need a value here are tested with this module mocked.
 */
import type { SurfaceEnvironment } from '@/contexts/SurfaceContext';
import { fetchKioskInfo, readKioskInfoFromStorage } from '@/services/kioskInfoCache';
import { hasNativeHost } from '@/platform/kioskHost';

/**
 * VITE_DOMAIN_TYPE=kiosk|mobile: the starting surface, so the first frame is right before
 * /api/kiosk/info answers (the server still wins when it answers kiosk or mobile). In dev, the
 * older VITE_DEV_DOMAIN_TYPE stands in when VITE_DOMAIN_TYPE is not set.
 */
export function domainTypeFlag(): string | undefined {
  const flag = import.meta.env.VITE_DOMAIN_TYPE?.trim();
  if (flag) return flag;
  return import.meta.env.DEV ? import.meta.env.VITE_DEV_DOMAIN_TYPE?.trim() || undefined : undefined;
}

/** `?surface=kiosk|mobile` is honoured only in dev or a UI preview build (as `?device=`). */
export function isSurfaceOverrideAllowed(): boolean {
  return Boolean(import.meta.env.DEV) || import.meta.env.VITE_UI_PREVIEW === 'true';
}

/** VITE_KIOSK_HEARTBEAT_INTERVAL_SECONDS (unset: the 60 s default; never below 60 s). */
export function kioskHeartbeatIntervalSeconds(): string | undefined {
  return import.meta.env.VITE_KIOSK_HEARTBEAT_INTERVAL_SECONDS;
}

/** The real detection inputs for SurfaceProvider and the pre-render boot (main.tsx). */
export function surfaceEnvironment(): SurfaceEnvironment {
  const host = () => globalThis.location?.hostname ?? '';
  return {
    flag: domainTypeFlag(),
    overrideAllowed: isSurfaceOverrideAllowed(),
    readCachedType: () => (host() ? readKioskInfoFromStorage(host())?.type : undefined),
    fetchServerType: () => fetchKioskInfo().then((payload) => payload?.type ?? null),
    nativeHost: hasNativeHost(),
  };
}
